import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { pageFor } from "../Navigation/pages";
import { octiotUrl } from "../common/images";
import { Toaster } from "react-hot-toast";
import "./Header.css";
import LogOut from "./LogOut";
import ChangePasswordDialog from "./ChangePasswordDialog";

function Header() {
  const [pop, setPop] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const navigate = useNavigate();
  // The page being shown, named on the left of the header.
  const page = pageFor(useLocation().pathname);

  const login = useSelector((state) => state.login);

  useEffect(() => {
    if (Object.keys(login).length === 0) navigate("/");
  }, [login]);
  const ref = useRef();
  const style = {
    headerContainer: {
      position: "sticky",
      top: 0,
      zIndex: 100,
      height: "clamp(56px, 8vh, 78px)",
      width: "100%",
      padding: "0 2%",
      backgroundColor: "#FFFFFF",
      // The header is sticky, so it needs its own edge against the content
      // scrolling under it.
      borderBottom: "1px solid #EDF0F5",
      display: "flex",
      // Navigation and the logo live in the sidebar; the header names the
      // page on the left and keeps the account controls on the right.
      justifyContent: "space-between",
      alignItems: "center",
      gap: "16px",
    },
    welcome: {
      color: "#212529",
      fontFamily: "manrope",
      fontSize: "22px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "24px",
    },
    login: {
      color: "#000",
      fontFamily: "manrope",
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "24px",
      marginRight: "8px",
    },
    avatar: {
      width: "36px",
      height: "36px",
      borderRadius: "50%",
      backgroundColor: "#0D6EFD",
      color: "#FFFFFF",
      fontFamily: "manrope",
      fontSize: "14px",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      textTransform: "uppercase",
    },
    profilePopup: {
      position: "absolute",
      top: "calc(100% + 10px)",
      right: 0,
      zIndex: 200,
      minWidth: "260px",
      padding: "16px",
      backgroundColor: "#FFFFFF",
      border: "1px solid #E9ECEF",
      borderRadius: "10px",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
      fontFamily: "manrope",
      textAlign: "left",
    },
    profileName: {
      color: "#212529",
      fontSize: "16px",
      fontWeight: 700,
      wordBreak: "break-word",
    },
    profileEmail: {
      color: "#6C757D",
      fontSize: "13px",
      wordBreak: "break-word",
    },
    profileDivider: {
      height: "1px",
      backgroundColor: "#E9ECEF",
      margin: "12px 0",
    },
    profileRowLabel: {
      color: "#6C757D",
      fontSize: "12px",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.4px",
    },
    profileRowValue: {
      color: "#212529",
      fontSize: "14px",
      wordBreak: "break-word",
    },
    profileAction: {
      fontFamily: "manrope",
      fontSize: "13.5px",
      fontWeight: 700,
      color: "#2050FF",
      cursor: "pointer",
      padding: "6px 8px",
      margin: "0 -8px",
      borderRadius: "8px",
    },
    statusBadge: {
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 700,
      textTransform: "capitalize",
    },
  };

  const profile = login?.profile;
  const fullName = [profile?.firstName, profile?.lastName]
    .filter(Boolean)
    .join(" ");
  // Falls back to the email the user typed, which is in login even when the
  // /v1/users/me call failed.
  const displayName = fullName || profile?.emailAddress || login?.emailAddress || "";
  const initials =
    [profile?.firstName, profile?.lastName]
      .filter(Boolean)
      .map((part) => part[0])
      .join("") || displayName.slice(0, 1);

  const styleAll = {
    width: document.URL.includes(octiotUrl) ? "70px" : "40.304px",
  };
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setPop(false);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);
  useEffect(() => {
    const faviconUpdate = async () => {
      //grab favicon element by ID
      const favicon = document.getElementById("favicon");

      //check count value, if below 0 we change href property to our red circle image path
      if (document.URL.includes(octiotUrl)) {
        favicon.href = `/OCTIOT.ico`;
        document.title = "Energy Savings";
      }
      //if above 0, we set back to green
      else {
        favicon.href = `/favicon.ico`;
      }
    };
    //run our function here
    faviconUpdate();
  }, []);

  return (
    <div style={{ ...style.headerContainer }}>
      <Toaster />
      {page ? (
        <div className="headerTitle">
          <span className="headerTitleIcon" aria-hidden="true">
            <page.Icon fontSize="small" />
          </span>
          <h1 className="headerTitleText">{page.label}</h1>
        </div>
      ) : (
        <div />
      )}
      <div className="headerRight" ref={ref}>
        <div
          className="d-flex gap-2 align-items-center"
          style={{ cursor: "pointer", minWidth: 0 }}
          onClick={() => setPop((isOpen) => !isOpen)}
          title={displayName}
        >
          <div style={style.avatar}>{initials}</div>
          <div className="userName">{displayName}</div>
        </div>
        <LogOut />

        {pop && (
          <div style={style.profilePopup}>
            <div className="d-flex gap-3 align-items-center">
              <div style={style.avatar}>{initials}</div>
              <div>
                <div style={style.profileName}>{displayName}</div>
                {profile?.emailAddress && (
                  <div style={style.profileEmail}>{profile.emailAddress}</div>
                )}
              </div>
            </div>

            {profile ? (
              <>
                <div style={style.profileDivider} />

                {profile.phoneNumber && (
                  <div style={{ marginBottom: "10px" }}>
                    <div style={style.profileRowLabel}>Phone</div>
                    <div style={style.profileRowValue}>
                      {profile.phoneNumber}
                    </div>
                  </div>
                )}

                {profile.roleName && (
                  <div style={{ marginBottom: "10px" }}>
                    <div style={style.profileRowLabel}>Role</div>
                    <div
                      style={{
                        ...style.profileRowValue,
                        textTransform: "capitalize",
                      }}
                    >
                      {profile.roleName}
                    </div>
                  </div>
                )}

                {profile.status && (
                  <div style={{ marginBottom: "10px" }}>
                    <div style={style.profileRowLabel}>Status</div>
                    <div
                      style={{
                        ...style.statusBadge,
                        backgroundColor:
                          profile.status === "active" ? "#D1E7DD" : "#F8D7DA",
                        color:
                          profile.status === "active" ? "#0F5132" : "#842029",
                      }}
                    >
                      {profile.status}
                    </div>
                  </div>
                )}

                {/* An empty stores array is not necessarily "no access" - this
                    account sees every store with one - so it is only shown when
                    the server actually scopes the user to some. */}
                {profile.stores?.length > 0 && (
                  <div>
                    <div style={style.profileRowLabel}>Stores</div>
                    <div style={style.profileRowValue}>
                      {profile.stores.length}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={style.profileDivider} />
                <div style={style.profileEmail}>
                  {profile === null
                    ? "Profile details could not be loaded."
                    : "Loading profile\u2026"}
                </div>
              </>
            )}

            <div style={style.profileDivider} />

            {/* Own-account action, so it belongs with the account details
                rather than on a page of its own. */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => {
                setPop(false);
                setPasswordOpen(true);
              }}
              style={style.profileAction}
            >
              Change password
            </div>
          </div>
        )}
      </div>

      <ChangePasswordDialog
        open={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        emailAddress={profile?.emailAddress || login?.emailAddress || ""}
      />
    </div>
  );
}

export default Header;
